import React from 'react';
import _get from 'lodash/get';
import { TFunction } from 'i18next';
import {
  SsoIntegration,
  SsoIntegrationProviderPresetEnum,
  SsoIntegrationStatusEnum,
} from 'graphql-common';
import { CellProps } from '@lib/components/Table/types';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import SsoDisplayStatusChip from '@lib/components/SsoDisplayStatusChip/SsoDisplayStatusChip';
import SsoProviderTypeChip from '@lib/components/SsoProviderTypeChip/SsoProviderTypeChip';
import SsoProviderNameCell from '@lib/components/Table/commonComponents/SsoProviderNameCell';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import getSsoProviderIcon from '@lib/utils/sso/getSsoProviderIcon';
import { APP_URLS } from 'constants/urls';
import getSsoPresetLabel from './getSsoPresetLabel';

type Args = {
  t: TFunction<'translation', undefined>;
};

function getSsoIconSrc(item: SsoIntegration): string {
  if (item.icon?.urls) {
    return (
      _get(item, 'icon.urls.small') ||
      _get(item, 'icon.urls.medium') ||
      _get(item, 'icon.urls.original') ||
      getSsoProviderIcon(item.providerPreset)
    );
  }
  return getSsoProviderIcon(item.providerPreset);
}

export default function getTableColumns({ t }: Args) {
  return [
    {
      id: 'name',
      Header: t('provider'),
      Cell: ({ item }: CellProps<SsoIntegration>) => (
        <SsoProviderNameCell
          iconSrc={getSsoIconSrc(item)}
          name={item.name || getSsoPresetLabel(item.providerPreset, t)}
          url={APP_URLS.dashboard.sso.view.getDynamicPath({
            pathParts: { id: item.id },
          })}
        />
      ),
      minWidth: 250,
      maxWidth: 400,
      width: 250,
      sortable: true,
    },
    {
      id: 'providerType',
      Header: t('type'),
      Cell: ({ item }: CellProps<SsoIntegration>) => (
        <SsoProviderTypeChip data={item} />
      ),
      minWidth: 120,
      maxWidth: 180,
      width: 120,
    },
    {
      id: 'displayStatus',
      Header: t('status'),
      Cell: ({ item }: CellProps<SsoIntegration>) =>
        item.status === SsoIntegrationStatusEnum.Deactivated ? (
          <Chip
            type={ChipTypes.chipGray}
            label={t('deactivated')}
            leftIcon="circle"
          />
        ) : (
          <SsoDisplayStatusChip data={item} />
        ),
      minWidth: 150,
      maxWidth: 200,
      width: 150,
    },
    {
      id: 'updatedAt',
      Header: t('last-updated'),
      accessorFn: ({ item }: CellProps<SsoIntegration>) =>
        item.updatedAt
          ? getFormattedDate(item.updatedAt, dateTimeFormatEU)
          : '-',
      Cell: (props: CellProps<SsoIntegration>) => <TextCell {...props} />,
      sortable: true,
      minWidth: 180,
      maxWidth: 250,
      width: 180,
    },
  ];
}
